import { Alert, Button, Empty, Input, Message, Modal, Spin, Switch, Tag } from "@arco-design/web-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Eye } from "lucide-react";
import { Pagination } from "../../../shared/ui/shadcn/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shared/ui/shadcn/table";
import "./feeAdjustmentRecords.css";

import type { FeeAdjustmentRecord } from "../../fee-adjustment";
import {
  useCreateFeeAdjustmentRequest,
  FeeAdjustmentRecordDetailModal,
} from "../../fee-adjustment";

export type FeeAdjustmentRequestTarget = {
  id: string;
  name: string;
  role: string;
};

/** 单人和多人共用的统一费用调整销售申请弹窗。 */
export function FeeAdjustmentRequestModal({
  visible,
  rosterId,
  members,
  onClose,
}: {
  visible: boolean;
  rosterId: string;
  members: FeeAdjustmentRequestTarget[];
  onClose: () => void;
}) {
  const mutation = useCreateFeeAdjustmentRequest(rosterId);
  const [requestText, setRequestText] = useState("");
  const [payerIds, setPayerIds] = useState<Set<string>>(() => new Set());
  const membersKey = JSON.stringify(members.map((member) => member.id));
  const sessionRef = useRef({ rosterId, membersKey, visible });
  if (sessionRef.current.rosterId !== rosterId || sessionRef.current.membersKey !== membersKey || sessionRef.current.visible !== visible) {
    sessionRef.current = { rosterId, membersKey, visible };
  }
  const session = sessionRef.current;
  const mountedRef = useRef(true);
  const submittingRef = useRef<typeof session | null>(null);
  const [submittingSession, setSubmittingSession] = useState<typeof session | null>(null);
  const submitting = submittingSession === session;
  const intentRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const currentSession = () => mountedRef.current && sessionRef.current === session;
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => {
    if (!visible) return;
    setRequestText("");
    setPayerIds(new Set(JSON.parse(membersKey) as string[]));
    intentRef.current = null;
  }, [membersKey, rosterId, visible]);
  const canSubmit = Boolean(rosterId && members.length && requestText.trim());

  /**
   *
   */
  async function submit() {
    if (!canSubmit || !visible || !currentSession() || submittingRef.current === session) return;
    const payload = {
      request_text: requestText.trim(),
      affected_members: members.map((member) => ({ roster_member_id: member.id, is_payer: payerIds.has(member.id) })),
    };
    const fingerprint = JSON.stringify(payload);
    if (intentRef.current?.fingerprint !== fingerprint) intentRef.current = { fingerprint, key: crypto.randomUUID() };
    submittingRef.current = session;
    setSubmittingSession(session);
    try {
      await mutation.mutateAsync({
        ...payload,
        idempotency_key: intentRef.current.key,
      });
      if (!currentSession()) return;
      intentRef.current = null;
      Message.success("费用调整需求已提交");
      onClose();
    } catch {
      if (currentSession()) Message.error("费用调整需求提交失败，请稍后重试");
    } finally {
      if (submittingRef.current === session) submittingRef.current = null;
      if (currentSession()) setSubmittingSession(null);
    }
  }

  return (
    <Modal
      title="费用调整申请"
      style={{ width: 520, maxWidth: "calc(100vw - 32px)" }}
      visible={visible}
      unmountOnExit
      onCancel={() => { if (currentSession() && submittingRef.current !== session) onClose(); }}
      closable={!submitting}
      maskClosable={!submitting}
      escToExit={!submitting}
      cancelButtonProps={{ disabled: submitting }}
      onOk={() => void submit()}
      okText="提交费用调整"
      cancelText="取消"
      confirmLoading={submitting}
      okButtonProps={{ disabled: !canSubmit || submitting }}
    >
      <div className="order-handover-fee-adjustment-request">
        <Alert
          type="info"
          content={members.length === 1
            ? "本次只调整当前成员；计调在确认件页面录入结构化费用明细。"
            : "本次成员范围已由名单勾选确定；选择不出钱的成员仍保留在请求快照中。"}
        />
        <div className="order-handover-fee-adjustment-request__members" aria-label="费用调整成员是否出钱">
          {members.map((member) => (
            <label key={member.id}>
              <span><strong>{member.name}</strong><small>{member.role}</small></span>
              <Switch
                disabled={submitting}
                checked={payerIds.has(member.id)}
                onChange={(checked) => setPayerIds((current) => {
                  if (submittingRef.current === session) return current;
                  if (current.has(member.id) !== checked) intentRef.current = null;
                  const next = new Set(current);
                  if (checked) next.add(member.id); else next.delete(member.id);
                  return next;
                })}
                checkedText="出钱"
                uncheckedText="不出钱"
                aria-label={`${member.name}是否出钱`}
              />
            </label>
          ))}
        </div>
        <label className="order-handover-fee-adjustment-request__text">
          <span>调整需求</span>
          <Input.TextArea
            aria-label="费用调整需求"
            value={requestText}
            disabled={submitting}
            placeholder="说明需要调整的服务、原因和计调需要核实的事实"
            autoSize={{ minRows: 4, maxRows: 8 }}
            onChange={(value) => {
              if (submittingRef.current === session) return;
              if (value.trim() !== requestText.trim()) intentRef.current = null;
              setRequestText(value);
            }}
          />
        </label>
      </div>
    </Modal>
  );
}

/** 展示名单在当前报价下的全部统一费用调整记录。 */
export function FeeAdjustmentRecords({
  records,
  loading,
  error,
}: {
  records: FeeAdjustmentRecord[];
  loading: boolean;
  error: boolean;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [requestedPage, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const sorted = useMemo(() => [...records].sort((left, right) => (
    right.created_at.localeCompare(left.created_at) || right.request_id.localeCompare(left.request_id)
  )), [records]);
  const page = Math.min(requestedPage, Math.max(1, Math.ceil(sorted.length / pageSize)));
  const selected = sorted.find((record) => record.request_id === selectedId) ?? null;
  return (
    <section className="handover-fee-records" aria-label="费用调整记录" aria-busy={loading}>
      <div className="handover-fee-records-heading">
        <strong>本团费用调整记录</strong>
        {(!loading && !error || sorted.length > 0) && <span>{sorted.length} 笔</span>}
      </div>
      {loading ? <div role="status"><Spin size={24} /> 正在加载费用调整记录...</div> : null}
      {error ? <Alert type="error" content="费用调整记录加载失败，请刷新后重试" /> : null}
      {!loading && !error && sorted.length === 0 ? <Empty description="暂无费用调整记录" /> : null}
      {sorted.length ? (
        <>
          <Table aria-label="本团费用调整记录" density="compact" containerClassName="handover-fee-records-table">
            <colgroup><col style={{ width: 110 }} /><col style={{ width: 190 }} /><col style={{ width: 155 }} /><col /><col style={{ width: 120 }} /><col style={{ width: 112 }} /></colgroup>
                <TableHead><TableRow>{["记录编号", "创建时间", "成员 / 是否出钱", "需求摘要", "状态", "操作"].map((title) => <TableHeader key={title}>{title}</TableHeader>)}</TableRow></TableHead>
            <TableBody>{sorted.slice((page - 1) * pageSize, page * pageSize).map((record) => (
              <TableRow key={record.request_id}>
                <TableCell>{shortRequestNo(record.request_id)}</TableCell>
                <TableCell>{formatDateTime(record.created_at)}</TableCell>
                <TableCell>{record.members.filter((member) => member.is_payer).length} 人出钱 / {record.members.length} 人</TableCell>
                <TableCell><span className="handover-fee-records-summary" title={record.request_text}>{record.request_text}</span></TableCell>
                <TableCell><Tag color={record.status === "processed" ? "green" : "arcoblue"}>{record.status === "processed" ? "计调处理完成" : "销售发起费用调整申请"}</Tag></TableCell>
                <TableCell><Button size="small" type="text" icon={<Eye size={14} />} onClick={() => setSelectedId(record.request_id)}>查看详情</Button></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
          <Pagination page={page} pageSize={pageSize} total={sorted.length} pageSizeOptions={[10, 20, 50]} onChange={(nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); }} />
        </>
      ) : null}
      <FeeAdjustmentRecordDetailModal target={selected ? { workspaceId: selected.fulfillment_workspace_id, requestId: selected.request_id } : null} onClose={() => setSelectedId(null)} />
    </section>
  );
}

/** 显示本地业务记录短编号。 */
function shortRequestNo(requestId: string): string {
  return requestId.slice(0, 8).toUpperCase();
}

/** 格式化实际申请时间。 */
function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}
